/**
 * Création d'un projet depuis l'interface (S1, ART-OUTIL-0132).
 *
 * Répond au « comment créer un nouveau projet ? » : l'amorce du cycle ne passe plus
 * par un INSERT SQL. Le créateur en devient propriétaire (côté passerelle). À la
 * réussite, le nouvel espace est rafraîchi ET sélectionné.
 *
 * v05x-t2 (ADR-0110 A6, SRF-PRJ-012) : ce dialogue est le PREMIER PAS de l'assistant de
 * lancement — identité et gabarit. Le projet naît en « lancement inachevé » et l'utilisateur
 * poursuit dans l'assistant (programme, choix technologiques hérités, équipe, récapitulatif).
 */
import { cloneElement, isValidElement, useId, useMemo, useState, type JSX, type MouseEvent, type ReactElement, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'

import { api, ApiError } from '@/api/client'
import { ErrorState } from '@/components/ErrorState'
import { useT } from '@/i18n/language'
import { useChangerContexte } from '@/hooks/useChangerContexte'
import { useMotifEcritureRefusee } from '@/hooks/useMotifEcritureRefusee'
import { useSession } from '@/hooks/useSession'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import { Champ, FormulaireChamps } from '@/components/ui/champ'
import { SelectNatif } from '@/components/ui/native-select'
import { useFormulaire } from '@/hooks/useFormulaire'
import { longueurMin } from '@/lib/validation'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const MIN = 3

/**
 * Deux modes d'usage :
 *  - non contrôlé (défaut) : rend un déclencheur (`trigger` ou bouton par défaut) et
 *    gère son ouverture — usage historique (carte du tableau de bord, switcher vide) ;
 *  - contrôlé (`open`/`onOpenChange`) : sans déclencheur, ouvert par le menu « Créer »
 *    global ou la palette ⌘K, montés ailleurs dans la coquille.
 */
export function NewWorkspaceDialog({ trigger, open: openProp, onOpenChange }: {
  trigger?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}): JSX.Element {
  const { t } = useT()
  const { choisirProjet } = useChangerContexte()
  const session = useSession()
  const idRefus = useId()
  const queryClient = useQueryClient()
  const controlled = openProp !== undefined
  const [openState, setOpenState] = useState(false)
  const open = controlled ? openProp : openState
  const setOpen = (o: boolean): void => {
    if (!controlled) setOpenState(o)
    onOpenChange?.(o)
  }
  const [templateId, setTemplateId] = useState('')

  // Templates de configuration (F1b, #115) : choisis à la création d'un projet, comme pour un
  // programme. Le « Default » intégré est la sélection par défaut tant qu'aucun autre n'est choisi.
  const templates = useQuery({ queryKey: ['templates'], queryFn: api.listTemplates })
  const templateDefaut = useMemo(
    () => (templates.data ?? []).find((tpl) => tpl.is_default) ?? (templates.data ?? [])[0],
    [templates.data])
  const templateChoisi = templateId || templateDefaut?.id || ''

  const f = useFormulaire({
    nom: {
      initial: '', label: t('newWs.nameLabel', 'Nom du projet'),
      obligatoire: true, regles: [longueurMin(MIN)],
      aide: t('newWs.nameAide', 'Ce nom apparaîtra dans le sélecteur de projet.'),
    },
    description: { initial: '', label: t('newWs.descriptionLabel', 'Description') },
  })

  const creer = useMutation({
    // La validation vivait DANS `mutationFn` sous forme d'exception — code mort, puisque le
    // bouton désactivé empêchait de l'atteindre. Elle est maintenant une règle, qui produit
    // un message visible sous le champ concerné.
    mutationFn: (v: { nom: string; description: string }) =>
      api.createWorkspace(v.nom.trim(), v.description.trim() || undefined,
                          templateChoisi || undefined, true),
    onSuccess: async (ws) => {
      await queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      // RJ-09 : l'indicateur « projets » du tableau de bord lit le portefeuille.
      await queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      // FND-V050-185 : le projet créé devient le contexte par le chemin UNIQUE (fournisseurs ET
      // ?projet=) — sinon la barre relisait l'ancien ?projet= et le rétablissait.
      // v05x-t2 : le projet existe ; le lancement continue dans l'assistant (pas 2 sur 5).
      choisirProjet(ws.workspace_id, { programme: ws.program_id ?? null, vers: '/org/projets?vue=lancement' })
      setOpen(false)
      f.reinitialiser(); setTemplateId('')
    },
    // Le dialogue se ferme : le déclencheur disparaît, donc toast (règle de `mutation-feedback`).
    // Le texte passe par `meta` plutôt que par un `toast()` local, sinon le cache global en
    // afficherait un second par-dessus.
    meta: { retour: 'toast', succes: t('newWs.createdLaunchToast', 'Projet créé — lancement inachevé : poursuivez l’assistant') },
  })

  // FND-V050-083 (rejeu caisse RJ-04) : créer un projet est une écriture que le serveur refuse
  // sans rôle de realm propriétaire/contributeur (403). Le déclencheur reste visible mais
  // refusé AVANT le clic, avec son motif — traduit (N-30 a) : le motif de `auth/session` est français.
  const refus = useMotifEcritureRefusee(session)
  if (refus) {
    if (controlled) {
      return (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('newWs.title', 'Nouvel espace de travail')}</DialogTitle>
              <DialogDescription data-testid="new-workspace-refus">{refus}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t('common.close', 'Fermer')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )
    }
    const bloquer = (e: MouseEvent): void => { e.preventDefault() }
    const declencheur = isValidElement(trigger)
      ? cloneElement(trigger as ReactElement<Record<string, unknown>>, {
        'aria-disabled': true, 'aria-describedby': idRefus, 'data-refuse': 'true', onClick: bloquer,
      })
      : (
        <Button size="sm" variant="outline" data-testid="new-workspace" data-refuse="true"
                aria-disabled="true" aria-describedby={idRefus} onClick={bloquer}>
          <Plus className="size-4" aria-hidden="true" />
          {t('newWs.triggerButton', 'Nouvel espace')}
        </Button>
      )
    return (
      <span className="inline-flex flex-col items-start gap-1">
        {declencheur}
        <span id={idRefus} className="max-w-prose text-xs text-muted-foreground" data-testid="new-workspace-refus">{refus}</span>
      </span>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) creer.reset() }}>
      {!controlled ? (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button size="sm" variant="outline" data-testid="new-workspace">
              <Plus className="size-4" aria-hidden="true" />
              {t('newWs.triggerButton', 'Nouvel espace')}
            </Button>
          )}
        </DialogTrigger>
      ) : null}
      <DialogContent>
        <FormulaireChamps f={f} onValide={(v) => creer.mutate(v)}>
          <DialogHeader>
            <DialogTitle>{t('newWs.title', 'Nouveau projet')}</DialogTitle>
            <DialogDescription>
              {t('newWs.descriptionLancement', 'Pas 1 sur 5 : identité et gabarit. Le projet existe dès ce pas, en « lancement inachevé » ; vous en devenez propriétaire et poursuivez le lancement (programme, choix technologiques, équipe).')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Champ champ={f.champs.nom}>
              {(c) => <Input {...c} data-testid="new-workspace-name" autoFocus
                placeholder={t('newWs.namePlaceholder', 'Ex. Modernisation du core assurance')} />}
            </Champ>

            <Champ champ={f.champs.description}>
              {(c) => <Input {...c} data-testid="new-workspace-description"
                placeholder={t('newWs.descriptionPlaceholder', 'À quoi sert ce projet ?')} />}
            </Champ>

            <div className="space-y-2">
              <Label htmlFor="ws-template">{t('newWs.templateLabel', 'Gabarit de configuration')}</Label>
              <SelectNatif id="ws-template" data-testid="new-workspace-template"
                      value={templateChoisi} disabled={templates.isPending}
                      onChange={(e) => setTemplateId(e.target.value)}>
                {(templates.data ?? []).map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.is_default ? t('newWs.templateDefaultOption', 'Par défaut (intégré)') : tpl.name}
                  </option>
                ))}
              </SelectNatif>
              <p className="text-xs text-muted-foreground">
                {t('newWs.templateHint',
                  'La façon de travailler (types d\'items, workflow, liaisons) de ce projet.')}
              </p>
            </div>

            {creer.isError ? (
              <ErrorState message={(creer.error as ApiError).message}
                          onRetry={() => creer.mutate(f.valeurs)} />
            ) : null}
          </div>

          <DialogFooter>
            <Button type="submit" data-testid="new-workspace-submit" disabled={creer.isPending}>
              {creer.isPending ? t('newWs.creating', 'Création…') : t('newWs.createAndLaunch', 'Créer et poursuivre le lancement')}
            </Button>
          </DialogFooter>
        </FormulaireChamps>
      </DialogContent>
    </Dialog>
  )
}
